import React, { useMemo } from 'react';
import {
  Drawer,
  Box,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCurrentUser } from '../services/auth';
import { Logout as LogoutIcon } from '@mui/icons-material';

interface MenuItem {
  id: string;
  desMenu: string;
  caminho: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface AppDrawerProps {
  open: boolean;
  onClose: () => void;
  drawerWidth?: number;
  menuItems?: MenuItem[];
}

const AppDrawer: React.FC<AppDrawerProps> = ({
  open,
  onClose,
  drawerWidth = 260,
  menuItems = [],
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useMemo(() => getCurrentUser(), []);

  const handleMenuClick = (path: string) => {
    // Garante navegação absoluta para evitar URLs aninhadas
    if (!path.startsWith('/')) {
      navigate(`/${path}`, { replace: true });
    } else {
      navigate(path, { replace: true });
    }
    onClose();
  };

  const handleLogout = () => {
    localStorage.removeItem('setKeyUser');
    window.location.href = '/login';
  };

  return (
    <Drawer
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true, // Melhor performance mobile
      }}
      sx={{
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          borderRight: 'none',
          boxShadow: '0 1px 3px 1px rgba(0,0,0,0.03)',
        },
      }}
    >
      {/* Header do Drawer */}
      <Toolbar
        sx={{
          background: 'linear-gradient(45deg, #eac0b7 30%, #f5d8d1 90%)',
          color: 'white',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 2,
          gap: 1,
        }}
      >
        <Box
          component="img"
          src="/static/Logo-B_pequena.png"
          alt="By Lust Lingerie"
          sx={{
            height: 45,
            maxWidth: '100%',
            objectFit: 'contain',
          }}
        />
        <Typography
          variant="h6"
          component="div"
          sx={{
            fontWeight: 'bold',
            textAlign: 'center',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          By Lust Lingerie
        </Typography>
      </Toolbar>

      {/* Lista de Menu */}
      <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
        <List sx={{ px: 1 }}>
          {menuItems.map((item) => (
            <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => handleMenuClick(item.caminho)}
                selected={location.pathname === item.caminho}
                disabled={item.disabled}
                sx={{
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(234, 192, 183, 0.08)',
                  },
                }}
              >
                {item.icon && (
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: location.pathname === item.caminho ? 'inherit' : 'primary.main',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                )}
                <ListItemText
                  primary={item.desMenu}
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: '0.875rem',
                      fontWeight: location.pathname === item.caminho ? 'bold' : 'normal',
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      <List sx={{ px: 1, py: 0.5, borderTop: '1px solid', borderColor: 'grey.200' }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              borderRadius: 1,
              color: 'error.main',
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.04)',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: 'error.main' }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText
              primary="Sair"
              sx={{
                '& .MuiListItemText-primary': {
                  fontSize: '0.875rem',
                  fontWeight: 'normal',
                },
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>

      {/* Footer do Drawer */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid rgba(0, 0, 0, 0.12)',
          backgroundColor: 'grey.50',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
            {currentUser?.userName?.[0]?.toUpperCase() || 'U'}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }} noWrap>
              {currentUser?.userName || 'Usuário Logado'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AppDrawer;
